import { IGlimpseReport, InsightKind, TransactionStat } from '../../customer-shared/models/IGlimpseReport';
import { unreachable } from '../../shared/utils/unreachable';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { isSubwayCustomer } from '../utils/subway.utils';

export type TemplateLanguage = 'english' | 'spanish';

export type AtRiskEvaluation = 'perfect' | 'very-good' | 'good' | 'high' | 'very-high';

export enum ReportConstants {
  EmployeeDrinking = 'Employee is drinking',
  EmployeeSmoking = 'Employee is smoking',
  EmployeeEating = 'Employee is eating',
  Theft = 'Theft',
  PhoneUsage = 'Phone usage',
  IncorrectRingUp = 'Incorrect ring-up',
  IncorrectGlassware = 'Incorrect glassware',
  Waste = 'Product waste',
}

@Injectable({
  providedIn: 'root',
})
export class TemplateService {
  public static DrinkingEatingCategories: string[] = [
    '6-inch',
    'Footlong',
    'Meal',
    'Lunch',
    'Salad',
    'Slider',
    'Bowl',
    'Soup',
    'Wrap',
    'Platter',
    'Chips',
    'Cookie',
    'Flatbread',
    'Mini',
    'Pizza',
  ];
  public availableTemplates = [
    { id: 'performance-en', name: 'Performance summary (English)' },
    { id: 'performance-es', name: 'Performance summary (Spanish)' },
    { id: 'performance-door-en', name: 'Performance summary Door (English)' },
    { id: 'performance-door-es', name: 'Performance summary Door (Spanish)' },
  ];

  public static es: Record<string, any> = {};
  spanishCustomers = [
    'Alboa',
    'Barezzito',
    'Cantina La 20',
    'Cuerno',
    "Grupo Anderson's",
    'Grupo Archipielago',
    'Grupo Carolo',
    'Janis',
    'Lulio Restaurante',
    'Mera Corporation',
    'Mochomos',
    'Senor Frogs',
    'Mexico Lindo',
    'Puerto Madero',
    'Grupo Cambalache',
    'La Rural',
    'Bandoneon',
  ];

  constructor(private translate: TranslateService) {
    this.translate.get('es').subscribe(translations => {
      TemplateService.es = translations;
    });
  }

  getImageReportInsights(imageReportsCount: number, customerName: string) {
    let isSpanish = this.isSpanish(customerName);

    let text = imageReportsCount > 1 ? 'Multiple events of employee non-compliance' : 'Employee non-compliance';

    if (isSpanish) text = TemplateService.es[text] ? TemplateService.es[text] : text;

    return '⛔️ ' + text;
  }

  getEmployeeNonComplianceLine(lang: TemplateLanguage, report: IGlimpseReport) {
    let reportConstantsAssigned = Object.assign(ReportConstants);
    return Object.keys(ReportConstants)
      .map(t => {
        return {
          t,
          count: report.items
            .filter(x => x.kind === InsightKind.Alert)
            .reduce((acc, curr) => {
              let drinkingInstances = curr.attachments.filter(a => a.name.startsWith(reportConstantsAssigned[t]));
              acc += drinkingInstances.length;
              return acc;
            }, 0),
        };
      })
      .filter(x => x.count > 0)
      .map(x => {
        let key = reportConstantsAssigned[x.t];
        let icon = this.resolveIcon(key);
        let instanceOf = this.translatePart('instance(s) of', report, lang, x.count >= 2);
        let translated = this.translatePart(key.replace(' is ', ' '), report, lang, x.count >= 2);
        let suffix = '';
        if (key === ReportConstants.EmployeeDrinking || key === ReportConstants.EmployeeEating)
          suffix =
            ' (' +
            this.getImageReportCategories(report, reportConstantsAssigned)
              .map(x => this.translatePart(x, report, lang))
              .join(', ') +
            ')';
        return `${icon} ${x.count} ${instanceOf} ${translated}` + suffix;
      })
      .join(' ');
  }

  translatePart(text: string, report: IGlimpseReport, lang?: TemplateLanguage, plural: boolean = false) {
    let isSpanish = this.isSpanish(report.customer);

    if ((lang && lang === 'spanish') || isSpanish) {
      let key = plural ? text + '(s)' : text;
      text = TemplateService.es[key] ? TemplateService.es[key] : text;
    }
    return text;
  }

  resolveIcon(type: string) {
    switch (type) {
      case ReportConstants.EmployeeDrinking:
        return '🫗';
      case ReportConstants.EmployeeSmoking:
        return '💨';
      case ReportConstants.EmployeeEating:
        return '🍲';
      case ReportConstants.Theft:
        return '🫳';
      case ReportConstants.PhoneUsage:
        return '📱';
      case ReportConstants.IncorrectGlassware:
      case ReportConstants.IncorrectRingUp:
        return '⭕';
      case ReportConstants.Waste:
        return '🗑️';
    }
  }

  pickSignificantExamples<
    T extends {
      count: number;
    },
  >(items: T[]): T[] {
    if (items.length === 0) return [];

    items.sort((a, b) => b.count - a.count);

    const problemCount = items.reduce((sum, emp) => sum + emp.count, 0);
    let problemCountThreshold = items[0].count;
    for (let i = 0, sum = 0; i < items.length; i++) {
      sum += items[i].count;
      if (sum >= problemCount / 2) {
        problemCountThreshold = items[i].count;
        break;
      }
    }
    return items.filter(item => item.count >= problemCountThreshold);
  }

  getLostStats(report: IGlimpseReport, lang: TemplateLanguage) {
    const lostCategories = Object.keys(
      report.allTransactions
        .filter(tx => tx.stat === TransactionStat.lost)
        .reduce(
          (cats, tx) => {
            const key = lang === 'spanish' && TemplateService.es[tx.cat] ? TemplateService.es[tx.cat] : tx.cat;
            cats[key] = true;
            return cats;
          },
          {} as Record<string, true>,
        ),
    );

    return {
      count: report.consolidation.nm.qty,
      pct: report.consolidation.nm.pct,
      examples: lostCategories.length <= 2 && lostCategories.length > 0 ? lostCategories : [],
      categoriesWithCount: report.allTransactions
        .filter(x => x.stat === TransactionStat.lost)
        .reduce(
          (acc, next) => {
            acc[next.cat] = +(acc[next.cat] ? acc[next.cat] : 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        ),
    };
  }

  getAtRiskEvaluation(pct: number): AtRiskEvaluation {
    if (pct === 0) {
      return 'perfect';
    } else if (pct < 0.05) {
      return 'very-good';
    } else if (pct < 0.1) {
      return 'good';
    } else if (pct < 0.2) {
      return 'high';
    } else if (pct >= 0.2) {
      return 'very-high';
    }
  }

  getAtRiskStats(lang: TemplateLanguage, report: IGlimpseReport) {
    const evaluation: Record<TemplateLanguage, Record<AtRiskEvaluation, string>> = {
      english: {
        perfect: 'Perfect',
        'very-good': 'Very good',
        good: 'Good',
        high: 'High',
        'very-high': 'Very high',
      },
      spanish: {
        perfect: 'perfecto',
        'very-good': 'muy bueno',
        good: 'bueno',
        high: 'alto',
        'very-high': 'muy alto',
      },
    };

    const count = report.consolidation.nc.qty;
    const pct = report.consolidation.nc.pct;

    const categoryStats = report.allTransactions
      .filter(tx => tx.stat === TransactionStat.atRisk)
      .reduce(
        (stats, tx) => {
          const key = lang === 'spanish' && TemplateService.es[tx.cat] ? TemplateService.es[tx.cat] : tx.cat;
          const stat = stats.find(s => s.cat === key);
          if (stat) {
            stat.count++;
          } else {
            stats.push({ cat: key, count: 1 });
          }
          return stats;
        },
        [] as { cat: string; count: number }[],
      );

    const significantExamples = this.pickSignificantExamples(categoryStats).map(ex => ex.cat);
    const exactExamples = categoryStats.length <= 2 && categoryStats.length > 0 ? categoryStats.map(stat => stat.cat) : [];

    let change = 0;
    let previousReport = [...report.trends].reverse().find(r => r.periodId < report.periodId);
    if (previousReport) {
      change = report.consolidation.nc.pct - previousReport.consolidation.nc.pct;
    }

    return {
      count,
      pct,
      evaluation: evaluation[lang][this.getAtRiskEvaluation(pct)] as AtRiskEvaluation,
      significantExamples,
      exactExamples,
      change,
    };
  }

  getPerformanceSummaryNoActivity(lang: TemplateLanguage): string {
    switch (lang) {
      case 'english':
        return `🔍 No transactions detected in this audit period`;
      case 'spanish':
        return `🔍 No se detectó transacciones en este período de auditoría`;

      default:
        unreachable(lang);
    }
  }

  getPerformanceSummaryLine1(lang: TemplateLanguage, report: IGlimpseReport): string {
    const lostStats = this.getLostStats(report, lang);
    const lostCategoriesPart = lostStats.examples.length > 0 ? `(${lostStats.examples.join(', ')})` : ``;
    switch (lang) {
      case 'english':
        switch (lostStats.count) {
          case 0:
            return `🎉 All audited items matched to POS ring-ups`;
          case 1:
            return `🚨 1 audited item ${lostCategoriesPart} not matched to POS ring-ups, resulting in ${(lostStats.pct * 100).toFixed(
              1,
            )}% lost!`;
          default:
            return `🚨 ${lostStats.count} audited items ${lostCategoriesPart} not matched to POS ring-ups, resulting in ${(
              lostStats.pct * 100
            ).toFixed(1)}% lost!`;
        }
      case 'spanish':
        switch (lostStats.count) {
          case 0:
            return `🎉 ¡Todos los artículos auditados coinciden con las transacciones del PDV!`;
          case 1:
            return `🚨 1 artículo auditado ${lostCategoriesPart} no coincide en los registros del PDV, resultando en una pérdida del ${(
              lostStats.pct * 100
            ).toFixed(1)}%`;
          default:
            return `🚨 ${
              lostStats.count
            } artículos auditados ${lostCategoriesPart} no coinciden con los registros del PDV, lo que resulta en una pérdida del ${(
              lostStats.pct * 100
            ).toFixed(1)}%`;
        }

      default:
        unreachable(lang);
    }
  }

  private getDoorPerformanceSummaryLine2(lang: TemplateLanguage, report: IGlimpseReport): string {
    const lostStats = this.getLostStats(report, lang);
    const categoriesCounts = Object.keys(lostStats.categoriesWithCount).map(x => {
      if (x.toLowerCase() === 'male' && lang == 'english') return `👕 ${lostStats.categoriesWithCount[x]}x ${x}`;
      if (x.toLowerCase() === 'male' && lang == 'spanish') return `👕 ${lostStats.categoriesWithCount[x]}x hombres`;
      if (x.toLowerCase() === 'female' && lang == 'english') return `👚 ${lostStats.categoriesWithCount[x]}x ${x}`;
      if (x.toLowerCase() === 'female' && lang == 'spanish') return `👚 ${lostStats.categoriesWithCount[x]}x mujeres`;
      return null;
    });

    switch (lang) {
      case 'english':
        switch (categoriesCounts.length) {
          case 0:
            return `🎉 ALL audited admissions were matched successfully to POS records`;
          case 1:
            return `${categoriesCounts[0]} audited admissions were not matched successfully to POS records, resulting in ${(
              lostStats.pct * 100
            ).toFixed(1)}% lost!`;
          default:
            return `${categoriesCounts[0]} & ${categoriesCounts[1]} audited admissions were not matched successfully to POS records,
           resulting in ${(lostStats.pct * 100).toFixed(1)}% lost!`;
        }
      case 'spanish':
        switch (categoriesCounts.length) {
          case 0:
            return `🎉 ¡Todos los entradas auditados coinciden con las transacciones del PDV!`;
          case 1:
            return `${
              categoriesCounts[0]
            } admisiones no fueron emparejadas exitosamente con los registros del PDV, resultando en una pérdida del ${(
              lostStats.pct * 100
            ).toFixed(1)}%`;
          default:
            return `${categoriesCounts[0]} & ${
              categoriesCounts[1]
            } admisiones no fueron emparejadas exitosamente con los registros del PDV, lo que resulta en una pérdida del ${(
              lostStats.pct * 100
            ).toFixed(1)}%`;
        }

      default:
        unreachable(lang);
    }
  }

  getPerformanceSummaryLine4(lang: TemplateLanguage) {
    switch (lang) {
      case 'english':
        return `🏁 Goal: 5% or less At-Risk - 0% LOST transactions`;
      case 'spanish':
        return `🏁 Objetivo: 5% o menos En riesgo - 0% transacciones PERDIDAS`;
      default:
        unreachable(lang);
    }
  }

  getPerformanceSummaryLine2(lang: TemplateLanguage, report: IGlimpseReport): string {
    const atRiskStats = this.getAtRiskStats(lang, report);

    const atRiskCategoriesPart = atRiskStats.exactExamples.length > 0 ? `(${atRiskStats.exactExamples.join(', ')})` : ``;

    let change = ``;
    switch (lang) {
      case 'english':
        if (atRiskStats.change > 0) {
          change = `, resulting in an increase of ${(atRiskStats.change * 100).toFixed(1)}%`;
        } else if (atRiskStats.change < 0) {
          change = `, resulting in a decrease of ${(-atRiskStats.change * 100).toFixed(1)}%`;
        }
        break;
      case 'spanish':
        if (atRiskStats.change > 0) {
          change = `, resultando en un incremento del ${(atRiskStats.change * 100).toFixed(1)}%`;
        } else if (atRiskStats.change < 0) {
          change = `, resultando en una disminución del ${(-atRiskStats.change * 100).toFixed(1)}%`;
        }
        break;
    }

    switch (lang) {
      case 'english':
        switch (atRiskStats.count) {
          case 0:
            return `⏰ ${atRiskStats.evaluation} At-risk result, all audited servings were rung up in time!`;
          case 1:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, 1 audited item ${atRiskCategoriesPart} rung up OR served late${change}`;
          default:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, ${atRiskStats.count} audited items ${atRiskCategoriesPart} rung up OR served late${change}`;
        }
      case 'spanish':
        switch (atRiskStats.count) {
          case 0:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ¡todas las ventas auditadas se registraron a tiempo!`;
          case 1:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, 1 artículo auditado ${atRiskCategoriesPart} fue servido O se registró tarde${change}`;
          default:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ${atRiskStats.count} artículos auditados ${atRiskCategoriesPart} fueron servidos o se registraron tarde${change}`;
        }
      default:
        unreachable(lang);
    }
  }

  private getDoorPerformanceSummaryLine1(lang: TemplateLanguage, report: IGlimpseReport): string {
    const atRiskStats = this.getAtRiskStats(lang, report);

    let change = ``;
    switch (lang) {
      case 'english':
        if (atRiskStats.change > 0) {
          change = `, resulting in an increase of ${(atRiskStats.change * 100).toFixed(1)}%`;
        } else if (atRiskStats.change < 0) {
          change = `, resulting in a decrease of ${(-atRiskStats.change * 100).toFixed(1)}%`;
        }
        break;
      case 'spanish':
        if (atRiskStats.change > 0) {
          change = `, resultando en un incremento del ${(atRiskStats.change * 100).toFixed(1)}%`;
        } else if (atRiskStats.change < 0) {
          change = `, resultando en una disminución del ${(-atRiskStats.change * 100).toFixed(1)}%`;
        }
        break;
    }

    switch (lang) {
      case 'english':
        switch (atRiskStats.count) {
          case 0:
            return `⏰ ${atRiskStats.evaluation} At-risk result, all audited admissions were rung up in time!`;
          case 1:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, 1 audited admission rung up late${change}`;
          default:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, ${atRiskStats.count} audited admissions rung up late${change}`;
        }
      case 'spanish':
        switch (atRiskStats.count) {
          case 0:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ¡todas las entradas auditadas se registraron a tiempo!`;
          case 1:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, 1 entrada auditado se registró tarde${change}`;
          default:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ${atRiskStats.count} entradas auditadas se registraron tarde${change}`;
        }
      default:
        unreachable(lang);
    }
  }

  getPerformanceSummaryLine3(lang: TemplateLanguage, report: IGlimpseReport): string {
    const atRiskStats = this.getAtRiskStats(lang, report);

    switch (lang) {
      case 'english':
        switch (atRiskStats.significantExamples.length) {
          case 0:
            return null;
          case 1:
            return `🔎 ${atRiskStats.significantExamples[0]} was mostly rung up OR served late`;
          default:
            // keep first 2 only
            return `🔎 ${atRiskStats.significantExamples.slice(0, 2).join(' & ')} were mostly rung-up OR served late`;
        }
      case 'spanish':
        switch (atRiskStats.significantExamples.length) {
          case 0:
            return null;
          case 1:
            return `🔎 ${atRiskStats.significantExamples[0]} fue más registrada O servida tarde`;
          default:
            // keep first 2 only
            return `🔎 ${atRiskStats.significantExamples.slice(0, 2).join(' y ')} fue más registrada O servida tarde`;
        }

      default:
        unreachable(lang);
    }
  }

  private getDoorPerformanceSummaryLine3(lang: TemplateLanguage, report: IGlimpseReport): string {
    const atRiskStats = this.getAtRiskStats(lang, report);

    switch (lang) {
      case 'english':
        switch (atRiskStats.significantExamples.length) {
          case 0:
            return null;
          case 1:
            return `🔎 ${atRiskStats.significantExamples[0]} was mostly rung up late`;
          default:
            return `🔎 ${atRiskStats.significantExamples.slice(0, 2).join(' & ')} were mostly rung-up late`;
        }
      case 'spanish':
        switch (atRiskStats.significantExamples.length) {
          case 0:
            return null;
          default:
            return `🔎 ${
              atRiskStats.significantExamples[0].toLowerCase() === 'male' ? 'Hombres' : 'Mujeres'
            } se registró tarde en mayor medida`;
        }

      default:
        unreachable(lang);
    }
  }

  private getSubwayPerformanceSummaryLine1(report: IGlimpseReport): string {
    if (!report.productionStats) return null;

    let footlongStat = report.productionStats.find(x => x.category === 'Footlong');
    let productionStat = footlongStat ?? report.productionStats.sort((a, b) => a.avgProductionTime - b.avgProductionTime)[0];

    if (!productionStat) return '';
    const sec = productionStat.avgProductionTime % 60;
    const min = Math.floor(productionStat.avgProductionTime / 60) % 60;

    return `🔎 ${productionStat.category} was served and rung up in an average service time of ${min}m ${sec}s`;
  }

  private getSubwayPerformanceSummaryLine2(report: IGlimpseReport, currencyCode: string): string {
    const total = report.upsellStats.opportunity.food + report.upsellStats.opportunity.drink + report.upsellStats.opportunity.snack;
    const perCustomer = total / report.upsellStats.customerCount;

    return `💰 ${total.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode,
    })} in total missed revenue opportunity this shift (or ${perCustomer.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode,
    })} per customer)`;
  }

  getPerformanceSummaryLine5(lang: TemplateLanguage, report: IGlimpseReport): string {
    if (report.consolidation.nc.pct < 0.1) {
      // good at-risk result, focus on the best performing employee(s)
      const compliantStats = report.allTransactions
        .filter(tx => tx.stat === TransactionStat.compliant)
        .reduce(
          (stats, tx) => {
            const stat = stats.find(s => s.server === tx.server);
            if (stat) {
              stat.count++;
            } else {
              stats.push({ server: tx.server, count: 1 });
            }
            return stats;
          },
          [] as { server: string; count: number }[],
        );

      switch (lang) {
        case 'english':
          switch (compliantStats.length) {
            case 0:
              return null;
            case 1: {
              const itemCountPart = compliantStats[0].count > 1 ? `${compliantStats[0].count} items` : `1 item`;
              return `🥇 ${compliantStats[0].server} rung up AND served ${itemCountPart} on time`;
            }
            default: {
              compliantStats.sort((a, b) => b.count - a.count);
              const keepTillIndex = compliantStats.findIndex((s, i) => i > 0 && s.count === compliantStats[0].count);
              if (keepTillIndex === -1) {
                const itemCountPart = compliantStats[0].count > 1 ? `${compliantStats[0].count} items` : `1 item`;
                return `🥇 ${compliantStats[0].server} rung up AND served ${itemCountPart} on time`;
              }
              return `🥇 ${compliantStats
                .slice(0, keepTillIndex)
                .map(s => s.server)
                .join(', ')} rung up AND served the most items on time`;
            }
            // keep first 2 only
          }
        case 'spanish':
          switch (compliantStats.length) {
            case 0:
              return null;
            case 1: {
              const itemCountPart = compliantStats[0].count > 1 ? `${compliantStats[0].count} artículos` : `1 artículo`;
              return `🥇 ${compliantStats[0].server} sirvió y registró  ${itemCountPart} a tiempo`;
            }
            default: {
              compliantStats.sort((a, b) => b.count - a.count);
              const keepTillIndex = compliantStats.findIndex((s, i) => i > 0 && s.count === compliantStats[0].count);
              if (keepTillIndex === -1) {
                const itemCountPart = compliantStats[0].count > 1 ? `${compliantStats[0].count} artículos` : `1 artículo`;
                return `🥇 ${compliantStats[0].server} registró y sirvió ${itemCountPart} a tiempo`;
              }
              return `🥇 ${compliantStats
                .slice(0, keepTillIndex)
                .map(s => s.server)
                .join(', ')} registró y sirvió la mayoría de los artículos a tiempo`;
            }
            // keep first 2 only
          }

        default:
          unreachable(lang);
      }
    } else {
      // bad at-risk result, focus on the worst performing employee(s)
      const atRiskStats = report.allTransactions
        .filter(tx => tx.stat === TransactionStat.atRisk)
        .reduce(
          (stats, tx) => {
            const stat = stats.find(s => s.server === tx.server);
            if (stat) {
              stat.count++;
            } else {
              stats.push({ server: tx.server, count: 1 });
            }
            return stats;
          },
          [] as { server: string; count: number }[],
        );

      switch (lang) {
        case 'english':
          switch (atRiskStats.length) {
            case 0:
              return null;
            case 1: {
              const itemCountPart = atRiskStats[0].count > 1 ? `${atRiskStats[0].count} items` : `1 item`;
              return `⚠️ ${atRiskStats[0].server} rung up OR served ${itemCountPart} late`;
            }
            default: {
              atRiskStats.sort((a, b) => b.count - a.count);
              const keepTillIndex = atRiskStats.findIndex((s, i) => i > 0 && s.count === atRiskStats[0].count);
              if (keepTillIndex === -1) {
                const itemCountPart = atRiskStats[0].count > 1 ? `${atRiskStats[0].count} items` : `1 item`;
                return `⚠️ ${atRiskStats[0].server} rung up OR served ${itemCountPart} late`;
              }
              return `⚠️ ${atRiskStats
                .slice(0, keepTillIndex)
                .map(s => s.server)
                .join(', ')} rung up OR served the most items late`;
            }
          }
        case 'spanish':
          switch (atRiskStats.length) {
            case 0:
              return null;
            case 1: {
              const itemCountPart = atRiskStats[0].count > 1 ? `${atRiskStats[0].count} artículos` : `1 artículo`;
              return `⚠️ ${atRiskStats[0].server} registró y sirvió ${itemCountPart} tarde`;
            }
            default: {
              atRiskStats.sort((a, b) => b.count - a.count);
              const keepTillIndex = atRiskStats.findIndex((s, i) => i > 0 && s.count === atRiskStats[0].count);
              if (keepTillIndex === -1) {
                const itemCountPart = atRiskStats[0].count > 1 ? `${atRiskStats[0].count} items` : `1 item`;
                return `⚠️ ${atRiskStats[0].server} registró y sirvió ${itemCountPart} tarde`;
              }
              return `⚠️ ${atRiskStats
                .slice(0, keepTillIndex)
                .map(s => s.server)
                .join(', ')} registró y sirvió la mayoría de los artículos tarde`;
            }
          }

        default:
          unreachable(lang);
      }
    }
  }

  getPerformanceSummary(lang: TemplateLanguage, report: IGlimpseReport, currencyCode: string): string {
    return (
      report.allTransactions.length === 0
        ? [this.getPerformanceSummaryNoActivity(lang)]
        : [
            this.getPerformanceSummaryLine1(lang, report),
            this.getPerformanceSummaryLine2(lang, report),
            this.getEmployeeNonComplianceLine(lang, report),
            ...(isSubwayCustomer(report.customerId)
              ? [this.getSubwayPerformanceSummaryLine1(report), this.getSubwayPerformanceSummaryLine2(report, currencyCode)]
              : [this.getPerformanceSummaryLine3(lang, report)]),
            this.getPerformanceSummaryLine5(lang, report),
            this.getPerformanceSummaryLine4(lang),
          ]
    )
      .filter(l => l !== null)
      .map(l => `<p>${l}</p>`)
      .join('\n');
  }

  getDoorPerformanceSummary(lang: TemplateLanguage, report: IGlimpseReport): string {
    return (
      report.allTransactions.length === 0
        ? [this.getPerformanceSummaryNoActivity(lang)]
        : [
            this.getDoorPerformanceSummaryLine1(lang, report),
            this.getDoorPerformanceSummaryLine2(lang, report),
            this.getDoorPerformanceSummaryLine3(lang, report),
          ]
    )
      .filter(l => l !== null)
      .map(l => `<p>${l}</p>`)
      .join('\n');
  }

  getSimpleSms(lang: TemplateLanguage, report: IGlimpseReport): string {
    switch (lang) {
      case 'spanish':
        return `El reporte de Glimpse para ${report.customer} ${report.location} / ${report.area} está listo 🔥 {link}`;
      case 'english':
      default:
        return `Your Glimpse report for ${report.customer} ${report.location} / ${report.area} is ready 🔥 {link}`;
    }
  }

  getGeneratedInsightSms(lang: TemplateLanguage, report: IGlimpseReport): string {
    let shortText = report.items.find(x => x.shortText)?.shortText;
    if (!shortText) return this.getSimpleSms(lang, report);

    return `${shortText} ${lang === 'spanish' ? 'en' : 'at'} ${report.customer} ${report.location} / ${report.area} 🔥 {link}`;
  }

  getSummarySmsPart1(lang: TemplateLanguage, report: IGlimpseReport): string {
    const lostStats = this.getLostStats(report, lang);

    const lostCategoriesPart = lostStats.examples.length > 0 ? ` (${lostStats.examples.join(', ')})` : ``;

    switch (lang) {
      case 'english':
        switch (lostStats.count) {
          case 0:
            return `🎉 All audited items matched to POS ring-ups`;
          case 1:
            return `🚨 1 audited item not matched to POS ring-ups${lostCategoriesPart}`;
          default:
            return `🚨 ${lostStats.count} audited items not matched to POS ring-ups${lostCategoriesPart}`;
        }
      case 'spanish':
        switch (lostStats.count) {
          case 0:
            return `🎉 Todos los artículos auditados coinciden con las transacciones del PDV`;
          case 1:
            return `🚨 1 artículo auditado ${lostCategoriesPart} no coincide con los registros del PDV`;
          default:
            return `🚨 ${lostStats.count} artículos auditados ${lostCategoriesPart} no coinciden con los registros del PDV`;
        }
      default:
        unreachable(lang);
    }
  }

  getDoorSummarySmsPart1(lang: TemplateLanguage, report: IGlimpseReport): string {
    const lostStats = this.getLostStats(report, lang);

    const categoriesCounts = Object.keys(lostStats.categoriesWithCount)
      .map(x => {
        if (x.toLowerCase() === 'male') return `${lostStats.categoriesWithCount[x]}x ${x}`;
        if (x.toLowerCase() === 'female') return `${lostStats.categoriesWithCount[x]}x ${x}`;
        return null;
      })
      .join(', ');

    switch (lang) {
      case 'english':
        switch (lostStats.count) {
          case 0:
            return `🎉 All audited admissions matched to POS ring-ups`;
          case 1:
            return `🚨 1 audited admissions not matched to POS ring-ups [${categoriesCounts}]`;
          default:
            return `🚨 ${lostStats.count} audited admissions not matched to POS ring-ups [${categoriesCounts}]`;
        }
      case 'spanish':
        switch (lostStats.count) {
          case 0:
            return `🎉 Todos los entradas auditados coinciden con las transacciones del PDV`;
          case 1:
            return `🚨 1 entrada auditado [${categoriesCounts}] no coincide con los registros del PDV`;
          default:
            return `🚨 ${lostStats.count} entradas auditados [${categoriesCounts}] no coinciden con los registros del PDV`;
        }
      default:
        unreachable(lang);
    }
  }

  getSummarySmsPart2(lang: TemplateLanguage, report: IGlimpseReport): string {
    const atRiskStats = this.getAtRiskStats(lang, report);
    switch (lang) {
      case 'english':
        switch (atRiskStats.count) {
          case 0:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, all audited servings were rung up in time!`;
          case 1:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, 1 audited item rung up or served late`;
          default:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, ${atRiskStats.count} audited items rung up or served late`;
        }
      case 'spanish':
      default:
        switch (atRiskStats.count) {
          case 0:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ¡todas las ventas auditadas se registraron a tiempo!`;
          case 1:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, 1 artículo auditado fue servido O se registró tarde`;
          default:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ${atRiskStats.count} artículos auditados fueron servidos o se registraron tarde`;
        }
    }
  }

  getDoorSummarySmsPart2(lang: TemplateLanguage, report: IGlimpseReport): string {
    const atRiskStats = this.getAtRiskStats(lang, report);
    switch (lang) {
      case 'english':
        switch (atRiskStats.count) {
          case 0:
            return `⏰ ${atRiskStats.evaluation} At-risk result, all audited admissions were rung up in time!`;
          case 1:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, 1 audited admission rung up late`;
          default:
            return `⏰ ${atRiskStats.evaluation} At-Risk result, ${atRiskStats.count} audited admissions rung up late`;
        }
      case 'spanish':
        switch (atRiskStats.count) {
          case 0:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ¡todas las entradas auditadas se registraron a tiempo!`;
          case 1:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, 1 entrada auditado fue registró tarde`;
          default:
            return `⏰ Resultado de riesgo ${atRiskStats.evaluation}, ${atRiskStats.count} entradas auditados fueron registraron tarde`;
        }
      default:
        unreachable(lang);
    }
  }

  getNoActivitySms(lang: TemplateLanguage, report: IGlimpseReport): string {
    const at = lang === 'spanish' ? 'en' : 'at';
    return `${this.getPerformanceSummaryNoActivity(lang)} ${at} '${report.customer} ${report.location} / ${report.area}' {link}`;
  }

  getSummarySms(lang: TemplateLanguage, report: IGlimpseReport, reportedImages?: number): string {
    if (report.allTransactions.length === 0) return this.getNoActivitySms(lang, report);

    let reportsPart = reportedImages ? this.getEmployeeNonComplianceLine(lang, report) : '';
    switch (lang) {
      case 'spanish':
        return `${this.getSummarySmsPart1(lang, report)} ${reportsPart} ${this.getSummarySmsPart2(lang, report)} en '${report.customer} ${
          report.location
        } / ${report.area}' {link}`;
      case 'english':
      default:
        return `${this.getSummarySmsPart1(lang, report)} ${reportsPart} ${this.getSummarySmsPart2(lang, report)} at '${report.customer} ${
          report.location
        } / ${report.area}' {link}`;
    }
  }

  getImageReportCategories(report: IGlimpseReport, reportConstantsAssigned: any) {
    let strings = Object.keys(ReportConstants).reduce((all, curr) => {
      report.items
        .filter(x => x.kind === InsightKind.Alert)
        .forEach(x => {
          let attachments = x.attachments
            .filter(a => a.name.startsWith(reportConstantsAssigned[curr]))
            .map(a => a.name.replace(reportConstantsAssigned[curr], '').trim());

          attachments.forEach(a => all.add(a));
        });
      return all;
    }, new Set<string>());

    let result: string[] = [];
    strings.forEach(function (item) {
      if (item) result.push(item);
    });
    return result.sort((a, b) => {
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    });
  }

  getDoorSummarySms(lang: TemplateLanguage, report: IGlimpseReport): string {
    if (report.allTransactions.length === 0) return this.getNoActivitySms(lang, report);

    switch (lang) {
      case 'spanish':
        return `${this.getDoorSummarySmsPart1(lang, report)} ${this.getDoorSummarySmsPart2(lang, report)} en '${report.customer} ${
          report.location
        } / ${report.area}' {link}`;
      case 'english':
      default:
        return `${this.getDoorSummarySmsPart1(lang, report)} ${this.getDoorSummarySmsPart2(lang, report)} at '${report.customer} ${
          report.location
        } / ${report.area}' {link}`;
    }
  }

  getSubwaySms(lang: TemplateLanguage, report: IGlimpseReport, currencyCode: string, reportedImages?: number) {
    if (report.allTransactions.length === 0) return this.getNoActivitySms(lang, report);

    let subwayLine = isSubwayCustomer(report.customerId)
      ? `${this.getSubwayPerformanceSummaryLine1(report) ?? ''} ${this.getSubwayPerformanceSummaryLine2(report, currencyCode) ?? ''}`
      : '';
    let reportsPart = reportedImages ? this.getEmployeeNonComplianceLine(lang, report) : '';
    return `${this.getSummarySmsPart1(lang, report)} ${reportsPart} ${subwayLine} at '${report.customer} ${report.location} / ${
      report.area
    }' {link}`;
  }

  suggestTemplate(customerName: string, locationName: string, areaName: string) {
    let isSpanish = this.isSpanish(customerName);
    const languageSuffix = isSpanish ? 'es' : 'en';
    return `performance${areaName.toLowerCase().includes('door') ? '-door' : ''}-${languageSuffix}`;
  }

  isSpanish(customerName: string) {
    return this.spanishCustomers.some(x => x.toLowerCase().includes(customerName.toLowerCase()));
  }
}
